/**
 * StatsModal — the sales-demo "wow" dashboard. Leads with projected event
 * revenue and what poor planning leaves on the table (captured vs lost), backed
 * by an operational command view. All figures are pure-derived from the planner
 * state + pricing rate card and re-scope instantly to the selected period.
 */
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useEventStats } from '../store/eventPlannerStore'
import { REVENUE_LINES } from '../data/eventPricing'
import { formatShort } from '../data/formatTime'
import { PeriodSelector } from './PeriodSelector'
import { Donut, BarList, TrendChart, ProgressBar, Legend, InfoDot, CHART_COLORS, formatUsd, formatNum } from './charts'

/** One-line explanations for the derived metrics (shown via an "(i)" popover). */
const HELP = {
  projected: 'Total revenue if every aircraft in this period were placed — captured plus lost.',
  captured: 'Revenue from aircraft actually placed, billed at their zone’s rates.',
  lost: 'Projected revenue from demand that isn’t placed (overflow + not-yet-placed) — the capture opportunity.',
  peak: 'Highest number of aircraft parked at once. With time-aware slot reuse, total placed far exceeds this as slots recycle through the event.',
  avgPerAircraft: 'Captured revenue ÷ number of placed aircraft.',
  avgDwell: 'Average ground time (departure − arrival) across arrivals in this period.',
  quickTurn: 'Share of arrivals that stay under 8 hours (same-day quick turns).',
  lineCrew: 'Illustrative line-crew teams needed at peak (≈1 per 6 concurrent aircraft) — a planning estimate, not a staffing model.',
  zoneUtil: 'Peak concurrent aircraft ÷ estimated slot capacity per zone, scoped to the period.',
} as const

export function StatsModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const stats = useEventStats()

  const revLines = REVENUE_LINES.map((l) => ({ label: l.label, value: stats.captured[l.key] })).filter((d) => d.value > 0)
  const captureRate = stats.potential.total > 0 ? Math.round((stats.captured.total / stats.potential.total) * 100) : 0

  const flow = stats.flowCurve
  const flowSeries = [
    { label: 'Arr', color: '#38bdf8', values: flow.map((p) => p.arrivals) },
    { label: 'Dep', color: '#fbbf24', values: flow.map((p) => p.departures) },
    { label: 'On field', color: '#34d399', values: flow.map((p) => p.onField) },
  ]
  const xLabels: [string, string] = [formatShort(stats.period.start), formatShort(stats.period.end)]
  const fuelTotal = stats.jetAGallons + stats.avgasGallons

  const triageData = [
    { label: 'Green', value: stats.triage.green, color: '#34d399' },
    { label: 'Amber', value: stats.triage.amber, color: '#fbbf24' },
    { label: 'Red', value: stats.triage.red, color: '#f87171' },
  ].filter((d) => d.value > 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-slate-950 text-white sm:max-w-5xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-3 pr-8">
          <div>
            <DialogTitle className="text-base font-bold text-white">Event Statistics</DialogTitle>
            <p className="text-[11px] text-white/40">KAGS · Masters surge — projected revenue & operations</p>
          </div>
          <PeriodSelector />
        </div>

        <Tabs defaultValue="overview" className="mt-2">
          <TabsList className="bg-white/5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="operational">Operational</TabsTrigger>
          </TabsList>

          {/* ───────────── Overview ───────────── */}
          <TabsContent value="overview" className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Kpi label="Projected revenue" value={formatUsd(stats.potential.total, true)} accent="text-sky-300" info={HELP.projected} />
              <Kpi label="Captured" value={formatUsd(stats.captured.total, true)} sub={`${captureRate}% of potential`} accent="text-emerald-300" info={HELP.captured} />
              <Kpi label="Lost / turned away" value={formatUsd(stats.lost.total, true)} sub={`${stats.turnAways} aircraft`} accent="text-red-300" info={HELP.lost} />
              <Kpi label="Peak on field" value={formatNum(stats.peakConcurrent)} sub={`${formatNum(stats.placedCount)} placed`} info={HELP.peak} />
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <Panel title="Fleet mix">
                <div className="flex flex-col items-center gap-3">
                  <Donut data={stats.fleetMixByTier} centerValue={formatNum(stats.arrivalsInPeriod)} centerLabel="Arrivals" />
                  <Legend items={stats.fleetMixByTier} />
                </div>
              </Panel>
              <Panel title="Revenue by line item">
                <BarList data={revLines} format={(n) => formatUsd(n, true)} />
              </Panel>
              <Panel title="Arrivals vs departures">
                <TrendChart series={flowSeries} xLabels={xLabels} formatVal={formatNum} />
                <div className="mt-1 text-[10px] text-white/40">
                  Busiest arrival window: {formatShort(stats.busiestHour)}
                </div>
              </Panel>
            </div>
          </TabsContent>

          {/* ───────────── Financial ───────────── */}
          <TabsContent value="financial" className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Kpi label="Projected" value={formatUsd(stats.potential.total, true)} accent="text-sky-300" info={HELP.projected} />
              <Kpi label="Captured" value={formatUsd(stats.captured.total, true)} accent="text-emerald-300" info={HELP.captured} />
              <Kpi label="Lost" value={formatUsd(stats.lost.total, true)} sub={`${stats.turnAways} turned away`} accent="text-red-300" info={HELP.lost} />
              <Kpi label="Avg / aircraft" value={formatUsd(stats.avgRevenuePerAircraft, true)} info={HELP.avgPerAircraft} />
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Panel title="Captured revenue by line item">
                <BarList data={revLines} format={(n) => formatUsd(n, true)} />
              </Panel>
              <Panel title="Revenue by aircraft tier">
                <BarList data={stats.revenueByTier} format={(n) => formatUsd(n, true)} />
              </Panel>
              <Panel title="Revenue by client group">
                <BarList data={stats.revenueByClientGroup} format={(n) => formatUsd(n, true)} />
              </Panel>
              <Panel title="Revenue by program">
                <BarList data={stats.revenueByProgram} format={(n) => formatUsd(n, true)} />
              </Panel>
            </div>

            <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-200">
              <span className="font-bold">{formatUsd(stats.lost.total)}</span> in projected revenue is unplaced across{' '}
              <span className="font-bold">{stats.turnAways}</span> aircraft — capacity left on the table this period.
            </div>
          </TabsContent>

          {/* ───────────── Operational ───────────── */}
          <TabsContent value="operational" className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              <Kpi label="Arrivals" value={formatNum(stats.arrivalsInPeriod)} />
              <Kpi label="Peak on field" value={formatNum(stats.peakConcurrent)} info={HELP.peak} />
              <Kpi label="Avg dwell" value={`${stats.avgDwellHours.toFixed(1)}h`} info={HELP.avgDwell} />
              <Kpi label="Quick-turn" value={`${stats.quickTurnPct}%`} info={HELP.quickTurn} />
              <Kpi label="Line crew @ peak" value={formatNum(stats.lineCrewAtPeak)} info={HELP.lineCrew} />
              <Kpi label="VIP" value={formatNum(stats.vipCount)} />
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <Panel title="Flow over period">
                <TrendChart series={flowSeries} xLabels={xLabels} formatVal={formatNum} />
                <div className="mt-1.5"><Legend items={flowSeries.map((s) => ({ label: s.label, value: 0, color: s.color }))} /></div>
              </Panel>
              <Panel title="Fuel demand">
                <div className="grid grid-cols-3 gap-2">
                  <Kpi label="Jet-A gal" value={formatNum(stats.jetAGallons)} accent="text-sky-300" />
                  <Kpi label="Avgas gal" value={formatNum(stats.avgasGallons)} accent="text-emerald-300" />
                  <Kpi label="Truck trips" value={formatNum(stats.fuelTruckDispatches)} />
                </div>
                <div className="mt-3 text-[11px] text-white/40">Total uplift</div>
                <div className="text-lg font-bold tabular-nums text-white">{formatNum(fuelTotal)} gal</div>
              </Panel>
              <Panel title="Triage / risk split">
                <BarList data={triageData} format={formatNum} />
              </Panel>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Panel title="Zone utilization (peak ÷ capacity)" info={HELP.zoneUtil}>
                <div className="space-y-2">
                  {stats.zoneUtil.map((z) => (
                    <div key={z.areaId}>
                      <div className="mb-0.5 flex items-center justify-between text-[11px]">
                        <span className="truncate text-white/70">{z.name}</span>
                        <span className="tabular-nums text-white/90">
                          {z.peak}/{z.capacity} · {z.pct}%
                        </span>
                      </div>
                      <ProgressBar pct={z.pct} />
                    </div>
                  ))}
                  {stats.zoneUtil.length === 0 && <div className="text-[11px] text-white/30">No zones loaded.</div>}
                </div>
              </Panel>
              <Panel title="Service demand">
                <BarList data={stats.serviceDemand} format={formatNum} color={CHART_COLORS[1]} />
              </Panel>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

function Kpi({ label, value, sub, accent, info }: { label: string; value: string; sub?: string; accent?: string; info?: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-white/40">
        {label}
        {info && <InfoDot text={info} />}
      </div>
      <div className={cn('text-xl font-bold tabular-nums', accent ?? 'text-white')}>{value}</div>
      {sub && <div className="text-[10px] text-white/40">{sub}</div>}
    </div>
  )
}

function Panel({ title, children, className, info }: { title: string; children: React.ReactNode; className?: string; info?: string }) {
  return (
    <div className={cn('rounded-xl border border-white/10 bg-black/20 p-3', className)}>
      <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-white/40">
        {title}
        {info && <InfoDot text={info} />}
      </div>
      {children}
    </div>
  )
}
